<script>
import { GlAlert, GlFormGroup, GlFormInput, GlLoadingIcon, GlLink } from '@gitlab/ui';
import { formatMoney } from '~/javascripts/lib/format_money';
import { sprintf } from '~/javascripts/lib/locale';
import { MIN_VALIDATION_MESSAGE, OVERAGE_BILLING_LINK } from '~/javascripts/constants';
import {
  INVALID_NUMBER_MESSAGE,
  MAX_SEATS,
  MAX_SEATS_MESSAGE,
  PurchaseDetailErrors,
} from '../constants';

export default {
  name: 'PurchaseSubscriptionDetails',
  components: {
    GlAlert,
    GlFormGroup,
    GlFormInput,
    GlLoadingIcon,
    GlLink,
  },
  OVERAGE_BILLING_LINK,
  props: {
    title: {
      type: String,
      required: false,
      default: '',
    },
    subtitle: {
      type: String,
      required: false,
      default: '',
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    purchaseQuantity: {
      type: Number,
      required: false,
      default: 1,
    },
    purchaseQuantityLabel: {
      type: String,
      required: false,
      default: '',
    },
    purchaseQuantityReadonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    initialMinQuantityCount: {
      type: Number,
      required: false,
      default: 0,
    },
    minQuantityValidationMsg: {
      type: String,
      required: false,
      default: '',
    },
    unitPrice: {
      type: Number,
      required: false,
      default: 0,
    },
    unitDescription: {
      type: String,
      required: false,
      default: '',
    },
    subTotal: {
      type: Number,
      required: false,
      default: 0,
    },
    subTotalLabel: {
      type: String,
      required: false,
      default: '',
    },
    showOverageAlert: {
      type: Boolean,
      required: false,
      default: false,
    },
    testSelector: {
      type: String,
      required: false,
      default: 'user',
    },
  },
  data() {
    return {
      quantityError: '',
    };
  },
  computed: {
    quantityErrorState() {
      return !this.quantityError;
    },
    formattedSubTotal() {
      const isQuantityInvalid = this.quantityError || !this.purchaseQuantity;
      return formatMoney(this.subTotal, isQuantityInvalid);
    },
    shouldShowUnitPriceLoader() {
      if (this.isLoading) {
        return true;
      }

      return this.unitPrice !== 0 && !this.unitPrice;
    },
  },
  methods: {
    formatMoney,
    clearErrors() {
      this.quantityError = '';
    },
    validateInput(inputValue) {
      this.clearErrors();
      const numberValue = Number(inputValue);

      if (this.isInvalidNumber(inputValue, numberValue)) {
        return this.getValidationMessage(PurchaseDetailErrors.INVALID_NUMBER_ERROR);
      }
      if (numberValue > MAX_SEATS) {
        return this.getValidationMessage(PurchaseDetailErrors.MAX_SEATS_ERROR);
      }
      if (numberValue < this.initialMinQuantityCount) {
        return this.getValidationMessage(PurchaseDetailErrors.MIN_SEATS_ERROR);
      }

      return '';
    },
    isInvalidNumber(inputValue, numberValue) {
      return (
        inputValue === '' ||
        inputValue === null ||
        Number.isNaN(numberValue) ||
        !Number.isInteger(numberValue)
      );
    },
    getValidationMessage(error) {
      switch (error) {
        case PurchaseDetailErrors.MAX_SEATS_ERROR:
          return MAX_SEATS_MESSAGE;
        case PurchaseDetailErrors.INVALID_NUMBER_ERROR:
          return sprintf(INVALID_NUMBER_MESSAGE, { label: this.purchaseQuantityLabel });
        case PurchaseDetailErrors.MIN_SEATS_ERROR:
          return (
            this.minQuantityValidationMsg ||
            sprintf(MIN_VALIDATION_MESSAGE, {
              minValue: this.initialMinQuantityCount,
            })
          );
        default:
          return '';
      }
    },
    handleQuantityChange(inputValue) {
      this.quantityError = this.validateInput(inputValue);

      if (this.quantityError) {
        this.$emit('on-invalid-quantity');
        return;
      }

      this.$emit('quantity-change', Number(inputValue));
    },
  },
};
</script>

<template>
  <div>
    <h3 v-if="title" data-testid="purchase-subscription-details-title" class="gl-mb-5">
      {{ title }}
    </h3>
    <div v-if="subtitle" class="gl-mt-3 gl-text-sm gl-leading-20 gl-text-gray-500">
      {{ subtitle }}
    </div>
    <slot name="description"></slot>
    <div class="gl-pt-5">
      <div class="gl-flex gl-justify-between">
        <div class="gl-flex gl-items-center">
          <gl-form-group
            :label="purchaseQuantityLabel"
            :invalid-feedback="quantityError"
            :state="quantityErrorState"
            label-for="quantity"
          >
            <gl-form-input
              id="quantity"
              :value="purchaseQuantity"
              type="number"
              class="gl-inline gl-w-15"
              required
              :data-testid="`${testSelector}-quantity`"
              :state="quantityErrorState"
              :min="initialMinQuantityCount"
              :disabled="isLoading"
              :readonly="purchaseQuantityReadonly"
              @change="handleQuantityChange"
            />
            <span v-if="unitDescription" class="gl-pt-3" data-testid="unit-price-group">
              <span class="gl-px-5">&times;</span>
              <span data-testid="unit-price">
                <gl-loading-icon v-if="shouldShowUnitPriceLoader" class="gl-mr-3" inline />
                <span v-else>{{ formatMoney(unitPrice) }}</span>
                {{ unitDescription }}
              </span>
            </span>
          </gl-form-group>
        </div>
        <div v-if="subTotalLabel" class="gl-text-right">
          <p class="gl-m-0 gl-font-bold">{{ subTotalLabel }}</p>
          <gl-loading-icon v-if="isLoading" class="gl-relative gl-top-4" inline />
          <p
            v-else
            class="gl-m-0 gl-text-lg gl-leading-36 gl-text-gray-900"
            :data-testid="`${testSelector}-subtotal`"
          >
            {{ formattedSubTotal }}
          </p>
        </div>
      </div>
      <slot name="discount-message"></slot>
      <gl-alert
        v-if="showOverageAlert"
        variant="info"
        :dismissible="false"
        data-testid="overage-alert"
        class="gl-mb-5"
      >
        {{ __('You are billed additionally if you exceed the number of seats entered.') }}
        <gl-link :href="$options.OVERAGE_BILLING_LINK" target="_blank">
          {{ __('How does billing work ?') }}
        </gl-link>
      </gl-alert>

      <slot name="promo-code"></slot>
      <slot name="promo-code-success-message"></slot>
    </div>
  </div>
</template>
